import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Pagination,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { TravelerCaptureProposal, TravelerCaptureSource } from '@bandido/shared/types';
import { CaptureReview } from './CaptureReview';
import { CaptureSourceViewer } from './CaptureSourceViewer';
import { CaptureSourceThumbnail } from './CaptureSourceThumbnail';
import { captureAnalysisErrorKey } from './errors';
import { useAnalyzeCapture, useCaptureProposals, useCaptureSources, useDismissCapture } from './queries';

export const CaptureDialog = ({ caseId, onClose }: { caseId: number; onClose: () => void }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);
  const [review, setReview] = useState<TravelerCaptureProposal>();
  const [source, setSource] = useState<TravelerCaptureSource>();
  const sources = useCaptureSources(caseId);
  const proposals = useCaptureProposals(caseId, page);
  const analyze = useAnalyzeCapture(caseId);
  const dismiss = useDismissCapture(caseId);
  const [dismissTarget, setDismissTarget] = useState<string>();
  const busy = analyze.isLoading || dismiss.isLoading;
  const analyzeSelected = async () => {
    try {
      await analyze.mutateAsync(selected);
      setTab(1);
      setPage(1);
    } catch {
      /* Inline error preserves selection for retry. */
    }
  };
  const dismissSelected = async () => {
    if (!dismissTarget) return;
    try {
      await dismiss.mutateAsync(dismissTarget);
      setDismissTarget(undefined);
      setPage(1);
    } catch {
      /* Inline error. */
    }
  };
  const finishReview = () => {
    setReview(undefined);
    setPage(1);
    proposals.refetch();
  };
  return (
    <>
      <Dialog
        open
        onClose={busy || review ? undefined : onClose}
        fullWidth
        maxWidth={review ? 'xl' : 'lg'}
        fullScreen={fullScreen}
        PaperProps={{ sx: review ? { height: '90dvh', maxHeight: fullScreen ? '100dvh' : '90dvh' } : {} }}
      >
        <DialogTitle>{t('copilot.capture.title')}</DialogTitle>
        <DialogContent
          sx={review ? { display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 } : {}}
        >
          {review ? (
            <CaptureReview key={review.id} caseId={caseId} proposal={review} onDone={finishReview} />
          ) : (
            <>
              <Typography variant="body2" mb={2}>
                {t('copilot.capture.subtitle')}
              </Typography>
              <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="fullWidth">
                <Tab label={t('copilot.capture.sources')} />
                <Tab label={t('copilot.capture.pending', { count: proposals.data?.total ?? 0 })} />
              </Tabs>
              <Divider sx={{ mb: 2 }} />
              {dismiss.isError && <Alert severity="error">{t('copilot.capture.dismissError')}</Alert>}
              {tab === 0 ? (
                <>
                  {sources.isLoading && <CircularProgress />}
                  {sources.isError && (
                    <Alert
                      severity="error"
                      action={<Button onClick={() => sources.refetch()}>{t('common.retry')}</Button>}
                    >
                      {t('copilot.capture.sourceError')}
                    </Alert>
                  )}
                  {sources.data?.pages[0]?.enabled === false && (
                    <Alert severity="info">{t('copilot.capture.disabled')}</Alert>
                  )}
                  {sources.data?.pages
                    .flatMap((item) => item.items)
                    .map((item) => (
                      <Paper key={item.id} variant="outlined" sx={{ my: 1, p: 1.5, display: 'flex', gap: 2 }}>
                        {item.kind === 'file' && (
                          <CaptureSourceThumbnail caseId={caseId} source={item} onOpen={() => setSource(item)} />
                        )}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                            <FormControlLabel
                              label={item.label}
                              sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
                              control={
                                <Checkbox
                                  checked={selected.includes(item.id)}
                                  disabled={busy || (!selected.includes(item.id) && selected.length >= 8)}
                                  onChange={(_, checked) =>
                                    setSelected((previous) =>
                                      checked ? [...previous, item.id] : previous.filter((id) => id !== item.id),)
                                  }
                                />
                              }
                            />
                            <Chip size="small" label={item.channel} />
                            <Chip size="small" variant="outlined" label={t(`copilot.capture.kind.${item.kind}`)} />
                            <Button size="small" onClick={() => setSource(item)}>
                              {t('copilot.capture.viewSource')}
                            </Button>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(item.occurredAt).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                            {item.preview}
                          </Typography>
                        </Box>
                      </Paper>
                    ))}
                  {sources.data && !sources.data.pages.some((item) => item.items.length) && (
                    <Typography>{t('copilot.capture.noSources')}</Typography>
                  )}
                  {sources.hasNextPage && (
                    <Button disabled={sources.isFetchingNextPage} onClick={() => sources.fetchNextPage()}>
                      {t('copilot.capture.loadMore')}
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {proposals.isLoading && <CircularProgress />}
                  {proposals.isError && (
                    <Alert
                      severity="error"
                      action={<Button onClick={() => proposals.refetch()}>{t('common.retry')}</Button>}
                    >
                      {t('copilot.capture.loadError')}
                    </Alert>
                  )}
                  {proposals.data?.items.length === 0 && <Typography>{t('copilot.capture.noProposals')}</Typography>}
                  {analyze.isSuccess && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t(analyze.data.reused ? 'copilot.capture.reused' : 'copilot.capture.analyzed', {
                        count: analyze.data.proposalIds.length,
                      })}
                    </Alert>
                  )}
                  {proposals.data?.items.map((item) => (
                    <Paper key={item.id} variant="outlined" sx={{ p: 2, my: 1 }}>
                      <Typography variant="subtitle1">
                        {[item.candidate.fields.firstName?.value, item.candidate.fields.lastName?.value]
                          .filter(Boolean)
                          .join(' ') || t('copilot.capture.unnamed')}
                      </Typography>
                      <Typography variant="caption">{item.sources.map((item) => item.label).join(' · ')}</Typography>
                      <Stack direction="row" spacing={1} mt={1}>
                        <Button variant="outlined" disabled={busy} onClick={() => setReview(item)}>
                          {t('copilot.capture.review')}
                        </Button>
                        <Button color="error" disabled={busy} onClick={() => setDismissTarget(item.id)}>
                          {t('copilot.capture.dismiss')}
                        </Button>
                      </Stack>
                    </Paper>
                  ))}
                  {(proposals.data?.total ?? 0) > 25 && (
                    <Pagination
                      count={Math.ceil((proposals.data?.total ?? 0) / 25)}
                      page={page}
                      onChange={(_, value) => setPage(value)}
                    />
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        {!review && (
          <DialogActions
            sx={{ p: 2, borderTop: 1, borderColor: 'divider', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}
          >
            {analyze.isError && <Alert severity="error">{t(captureAnalysisErrorKey(analyze.error))}</Alert>}
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ width: '100%' }}>
              {tab === 0 && (
                <>
                  <Button
                    variant="contained"
                    onClick={analyzeSelected}
                    disabled={busy || !selected.length || !sources.data?.pages[0]?.enabled}
                  >
                    {t(analyze.isLoading ? 'copilot.capture.analyzing' : 'copilot.capture.analyze')}
                  </Button>
                  <Typography variant="caption" aria-live="polite">
                    {t('copilot.capture.selected', { count: selected.length })}
                  </Typography>
                </>
              )}
              <Box sx={{ flex: 1 }} />
              <Button onClick={onClose} disabled={busy}>
                {t('common.close')}
              </Button>
            </Stack>
          </DialogActions>
        )}
      </Dialog>
      {source && (
        <CaptureSourceViewer
          key={`${caseId}:${source.id}`}
          caseId={caseId}
          source={source}
          onClose={() => setSource(undefined)}
        />
      )}
      <Dialog open={Boolean(dismissTarget)} onClose={dismiss.isLoading ? undefined : () => setDismissTarget(undefined)}>
        <DialogTitle>{t('copilot.capture.dismiss')}</DialogTitle>
        <DialogContent>{t('copilot.capture.dismissHint')}</DialogContent>
        <DialogActions>
          <Button disabled={dismiss.isLoading} onClick={() => setDismissTarget(undefined)}>
            {t('common.cancel')}
          </Button>
          <Button disabled={dismiss.isLoading} color="error" onClick={dismissSelected}>
            {t('copilot.capture.dismiss')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
